import React, { useEffect, useMemo, useState, useCallback } from 'react';
import api from '../../lib/api';
import {
  BarChart3, TrendingUp, ShieldOff, Package, AlertTriangle,
  ClipboardList, Clock, Handshake, Loader2, Info,
} from 'lucide-react';

// Phase 9 · Sales Intelligence & Performance Analytics — READ-ONLY.
// Reads /api/sales-brain/analytics only. Never mutates. Zero LLM.

const RANGES = [
  ['today', 'Today'],
  ['7d', 'Last 7 days'],
  ['30d', 'Last 30 days'],
];

function KPI({ label, value, hint, testid, subtle = false }) {
  return (
    <div className="bg-white rounded-xl border border-neutral-200 p-3" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wider text-neutral-500 font-semibold">{label}</div>
      <div className={`mt-1 ${subtle ? 'text-lg text-neutral-700' : 'text-2xl text-neutral-900'} font-bold`}>
        {value == null || Number.isNaN(value) ? '—' : value}
      </div>
      {hint ? <div className="text-[11px] text-neutral-500 mt-0.5">{hint}</div> : null}
    </div>
  );
}

function Section({ title, icon: Icon, children, testid, right }) {
  return (
    <section className="bg-white rounded-xl border border-neutral-200 p-4" data-testid={testid}>
      <header className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          {Icon ? <Icon className="w-4 h-4 text-fab-primary" /> : null}
          <h3 className="text-sm font-semibold text-neutral-900">{title}</h3>
        </div>
        {right}
      </header>
      {children}
    </section>
  );
}

function Bar({ label, value, max, testid }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className="flex items-center gap-2" data-testid={testid}>
      <div className="w-40 shrink-0 text-[12px] text-neutral-700 truncate">{label}</div>
      <div className="flex-1 h-2 bg-neutral-100 rounded overflow-hidden">
        <div className="h-2 bg-fab-primary" style={{ width: `${pct}%` }} />
      </div>
      <div className="w-10 text-right text-[12px] font-medium text-neutral-800">{value}</div>
    </div>
  );
}

function List({ rows, testidPrefix, valueKey = 'count', labelKey = 'action', emptyText = 'No data yet' }) {
  const max = rows.reduce((m, r) => Math.max(m, r[valueKey] || 0), 0);
  if (!rows.length) {
    return <div className="text-[12px] text-neutral-500">{emptyText}</div>;
  }
  return (
    <div className="space-y-1.5">
      {rows.map((r, i) => (
        <Bar key={`${r[labelKey]}-${i}`}
             label={r[labelKey]} value={r[valueKey] || 0}
             max={max} testid={`${testidPrefix}-${r[labelKey]}`} />
      ))}
    </div>
  );
}

export default function AnalyticsPanel() {
  const [range, setRange] = useState('30d');
  const [data, setData] = useState(null);
  const [learning, setLearning] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Phase 13 · fetch analytics + learning summary in one bounded parallel
      // burst; both are seller-scoped, both use the selected date range.
      const [{ data: payload }, learn] = await Promise.all([
        api.get(`/sales-brain/analytics?range=${range}`),
        api.get(`/sales-brain/learning/summary?range=${range}`)
          .catch(() => ({ data: null })),
      ]);
      setData(payload);
      setLearning(learn?.data || null);
    } catch {
      setData(null); setLearning(null);
    } finally { setLoading(false); }
  }, [range]);

  useEffect(() => { load(); }, [load]);

  const summary = data?.summary || {};
  const funnel = data?.funnel || { by_intent: {}, by_state: {} };
  const nba = data?.nba || { by_action: [], by_priority: {} };
  const objections = data?.objections || { by_type: {}, by_resolution: {} };
  const policies = data?.policies || { by_type: {} };
  const recs = data?.recommendations || { by_status: {}, top_recommended_products: [] };
  const followups = data?.followups || { by_status: {}, executions: {} };
  const orders = data?.orders || {};
  const topProducts = data?.top_products?.items || [];

  const nbaRows = useMemo(() => (nba.by_action || []).map(a => ({ action: a.action, count: a.count })), [nba]);
  const objectionRows = useMemo(() => (
    Object.entries(objections.by_type || {})
      .filter(([, v]) => v > 0)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([k, v]) => ({ action: k, count: v }))
  ), [objections]);
  const funnelIntentRows = useMemo(() => (
    Object.entries(funnel.by_intent || {})
      .filter(([, v]) => v > 0)
      .sort((a, b) => b[1] - a[1])
      .map(([k, v]) => ({ action: k, count: v }))
  ), [funnel]);
  const followUpRows = useMemo(() => (
    Object.entries(followups.by_status || {})
      .filter(([, v]) => v > 0)
      .map(([k, v]) => ({ action: k, count: v }))
  ), [followups]);

  const conversionText = orders?.conversion_rate_percent == null
    ? 'Attribution unavailable'
    : `${orders.conversion_rate_percent}%`;

  return (
    <div className="space-y-3" data-testid="sb-analytics">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-fab-primary" />
          <h2 className="text-base font-semibold text-neutral-900">Sales Intelligence</h2>
        </div>
        <div className="inline-flex bg-neutral-100 rounded-lg p-1" data-testid="sb-analytics-range">
          {RANGES.map(([k, l]) => (
            <button key={k} onClick={() => setRange(k)}
                    className={`px-3 py-1 text-[12px] rounded-md ${range === k ? 'bg-white text-neutral-900 shadow-sm' : 'text-neutral-600'}`}
                    data-testid={`sb-analytics-range-${k}`}>{l}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8 text-neutral-500 gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading analytics…
        </div>
      ) : !data ? (
        <div className="py-8 text-center text-neutral-500 text-sm">No analytics data yet.</div>
      ) : (
        <>
          {/* TOP: 5 KPI cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <KPI testid="sb-kpi-total"
                 label="Total opportunities" value={summary.total_opportunities || 0} />
            <KPI testid="sb-kpi-high-intent"
                 label="High intent" value={summary.high_intent_count || 0} />
            <KPI testid="sb-kpi-orders"
                 label="Orders in window"
                 value={summary.orders_total_in_window || 0}
                 hint={summary.orders_attributed_to_sales_brain != null
                        ? `${summary.orders_attributed_to_sales_brain} attributed to Sales Brain`
                        : undefined} />
            <KPI testid="sb-kpi-conversion"
                 label="Conversion (Sales Brain)" value={conversionText}
                 hint="Only sourced orders are attributed" />
            <KPI testid="sb-kpi-handoffs"
                 label="Seller handoffs" value={summary.seller_intervention_count || 0} />
          </div>

          {/* SECOND: Intent funnel + NBA distribution */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <Section title="Intent funnel" icon={TrendingUp} testid="sb-section-intent">
              <List rows={funnelIntentRows} testidPrefix="sb-intent-row" />
            </Section>
            <Section title="Next Best Action distribution" icon={ClipboardList} testid="sb-section-nba">
              <List rows={nbaRows.slice(0, 8)} testidPrefix="sb-nba-row" />
            </Section>
          </div>

          {/* THIRD: Objections + Policy usage */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <Section title="Objections" icon={AlertTriangle} testid="sb-section-objections"
                     right={
                       <span className="text-[11px] text-neutral-500">
                         Verified: {objections.resolved_verified_facts || 0} · Handoff: {objections.seller_handoff_required || 0}
                       </span>
                     }>
              <List rows={objectionRows} testidPrefix="sb-objection-row" />
            </Section>
            <Section title="Policy usage" icon={ShieldOff} testid="sb-section-policies"
                     right={<span className="text-[11px] text-neutral-500">
                       Conflicts: {policies.conflicts || 0}
                     </span>}>
              <div className="space-y-2">
                {Object.entries(policies.by_type || {}).map(([t, b]) => (
                  <div key={t} className="border border-neutral-100 rounded-lg p-2"
                       data-testid={`sb-policy-${t}`}>
                    <div className="text-[12px] font-semibold text-neutral-800">{t.replace('_POLICY', '')}</div>
                    <div className="mt-1 grid grid-cols-4 gap-2 text-[11px] text-neutral-600">
                      <div>Checks: <b>{b.checks || 0}</b></div>
                      <div className="text-emerald-700">Applied: <b>{b.applied || 0}</b></div>
                      <div className="text-amber-700">Capped: <b>{b.capped_or_denied || 0}</b></div>
                      <div className="text-red-700">Conflict: <b>{b.conflict || 0}</b></div>
                    </div>
                  </div>
                ))}
                {policies.configured?.active_types?.length ? (
                  <div className="text-[11px] text-neutral-500">
                    Active: {policies.configured.active_types.join(', ')}
                  </div>
                ) : (
                  <div className="text-[11px] text-neutral-500">No policies enabled yet.</div>
                )}
              </div>
            </Section>
          </div>

          {/* FOURTH: Recommendations + Follow-ups */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <Section title="Recommendation performance" icon={Package} testid="sb-section-recs"
                     right={<span className="text-[11px] text-neutral-500">
                       Total: {recs.total || 0}
                     </span>}>
              <div className="space-y-2">
                {Object.entries(recs.by_status || {})
                  .filter(([, v]) => v > 0).map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between text-[12px] text-neutral-700"
                         data-testid={`sb-rec-status-${k}`}>
                      <span>{k}</span><b>{v}</b>
                    </div>
                  ))}
                {(recs.top_recommended_products || []).length > 0 && (
                  <div className="pt-2 border-t border-neutral-100">
                    <div className="text-[11px] uppercase tracking-wider text-neutral-500 font-semibold mb-1">Top products</div>
                    <List rows={(recs.top_recommended_products || []).map(p => ({
                      action: p.product_code, count: p.count,
                    }))} testidPrefix="sb-rec-top" />
                  </div>
                )}
              </div>
            </Section>
            <Section title="Follow-up pipeline" icon={Clock} testid="sb-section-followups"
                     right={<span className="text-[11px] text-neutral-500">
                       Sent {followups.executions?.sent || 0} · Blocked {followups.executions?.blocked || 0}
                     </span>}>
              <List rows={followUpRows} testidPrefix="sb-followup-row" />
              <div className="mt-2 text-[11px] text-neutral-500 flex items-start gap-1.5">
                <Info className="w-3 h-3 mt-0.5 shrink-0" />
                Follow-up execution kill switch is OFF — planning only, no messages sent.
              </div>
            </Section>
          </div>

          {/* FIFTH: Product interest ranking */}
          <Section title="Products by buyer interest" icon={Handshake} testid="sb-section-top-interest"
                   right={<span className="text-[11px] text-neutral-500">Top 10 in window</span>}>
            <List
              rows={topProducts.map(p => ({ action: p.product_code, count: p.count }))}
              testidPrefix="sb-top-product"
              emptyText="No product interest captured in this window."
            />
          </Section>

          {/* SIXTH: Phase 13 · Observed Learning Signals (read-only) */}
          <Section title="Observed Learning Signals" icon={BarChart3}
                   testid="sb-section-learning-signals"
                   right={<span className="text-[11px] text-neutral-500">Top 5 · seller history</span>}>
            <div className="text-[11px] text-neutral-500 pb-2 leading-snug">
              Historical observations from your own conversations only. Never a
              prediction, never a guarantee. Attribution is only claimed when
              the linkage was already verified — otherwise the row honestly
              reports it as unavailable.
            </div>
            {(() => {
              const items = (learning && Array.isArray(learning.items))
                ? learning.items : [];
              const top = items.slice(0, 5);
              const totalEvents = learning?.total_events ?? 0;
              if (learning === null) {
                return <div className="text-[12px] text-neutral-500"
                             data-testid="sb-learning-unavailable">
                  Learning summary unavailable.
                </div>;
              }
              if (top.length === 0) {
                return <div className="text-[12px] text-neutral-500"
                             data-testid="sb-learning-empty">
                  Not enough historical data yet.
                </div>;
              }
              return (
                <div className="space-y-2" data-testid="sb-learning-list">
                  {top.map((b, i) => (
                    <div key={`${b.event_type}-${b.event_key}-${i}`}
                         className="border border-neutral-100 rounded-lg p-2"
                         data-testid={`sb-learning-row-${b.event_type}-${b.event_key || 'na'}`}>
                      <div className="flex justify-between items-baseline">
                        <span className="text-[12px] font-medium text-neutral-800">
                          {b.event_type}: {b.event_key || '—'}
                        </span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                          b.confidence === 'HIGH' ? 'bg-emerald-50 text-emerald-700' :
                          b.confidence === 'MEDIUM' ? 'bg-amber-50 text-amber-700' :
                          b.confidence === 'LOW' ? 'bg-neutral-100 text-neutral-600' :
                          'bg-neutral-50 text-neutral-500'
                        }`}>{b.confidence || 'UNKNOWN'}</span>
                      </div>
                      <div className="mt-1 grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px] text-neutral-600">
                        <div>Observed: <b>{b.observations || 0}</b></div>
                        <div className="text-emerald-700">Successful: <b>{b.successes || 0}</b></div>
                        <div className="text-red-700">Failed: <b>{b.failures || 0}</b></div>
                        <div>Rate: <b>{b.observed_success_rate_percent == null
                          ? 'Insufficient data' : `${b.observed_success_rate_percent}%`}</b></div>
                        <div>Attribution avail: <b>{b.attribution_available || 0}</b></div>
                        <div>Attribution unavail: <b>{b.attribution_unavailable || 0}</b></div>
                      </div>
                    </div>
                  ))}
                  <div className="text-[11px] text-neutral-500 pt-1"
                       data-testid="sb-learning-total">
                    Total events observed: {totalEvents}
                  </div>
                </div>
              );
            })()}
          </Section>

          <div className="text-[11px] text-neutral-400 flex items-center gap-1 pt-1"
               data-testid="sb-analytics-footnote">
            <Info className="w-3 h-3" />
            {data.notes?.attribution || 'Read-only analytics — no messages sent.'}
          </div>
        </>
      )}
    </div>
  );
}
